import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import * as authService from '../services/auth/authService';
import { initAuthTabSync } from '../services/auth/authTabSync';
import { TOKEN_STORAGE_KEYS } from '../services/auth/tokenStorage';
import { MODULES_KEY } from '../services/session/sessionStore';
import { readLegacyCompatSession } from '../services/legacy/legacySession';

export const AuthContext = createContext(null);

const SHARED_STORAGE_KEYS = [...TOKEN_STORAGE_KEYS, MODULES_KEY];

function buildStateFromSession(session) {
  if (!session) {
    return {
      isAuthenticated: false,
      user: null,
      claims: null,
      alumnoCodigos: ''
    };
  }

  const legacy = session.legacy || readLegacyCompatSession();
  return {
    isAuthenticated: authService.isAuthenticated(),
    user: session.user,
    claims: authService.getClaims(),
    alumnoCodigos: legacy.alumnoCodigos || ''
  };
}

export function AuthProvider({ children }) {
  const [sessionReady, setSessionReady] = useState(false);
  const [state, setState] = useState(() => buildStateFromSession(authService.prepareTabSession()));

  const refreshFromSession = useCallback(() => {
    const session = authService.restoreSession();
    setState(buildStateFromSession(session));
  }, []);

  useEffect(() => {
    let active = true;

    const stopTabSync = initAuthTabSync((event) => {
      if (!active) {
        return;
      }
      if (event?.loggedOut) {
        authService.clearSession({ skipBroadcast: true });
        setState({
          isAuthenticated: false,
          user: null,
          claims: null,
          alumnoCodigos: ''
        });
        if (typeof window !== 'undefined' && window.location.pathname !== '/') {
          window.location.assign('/');
        }
        return;
      }
      refreshFromSession();
    });

    authService.bootstrapSession()
      .catch(() => null)
      .finally(() => {
        if (active) {
          refreshFromSession();
          setSessionReady(true);
        }
      });

    const onAuthenticated = () => refreshFromSession();
    const onLogout = () => {
      setState({
        isAuthenticated: false,
        user: null,
        claims: null,
        alumnoCodigos: ''
      });
    };
    const onStorage = (event) => {
      if (!active || !event.key || !SHARED_STORAGE_KEYS.includes(event.key)) {
        return;
      }

      if (event.newValue) {
        sessionStorage.setItem(event.key, event.newValue);
      } else {
        sessionStorage.removeItem(event.key);
      }

      authService.syncSessionFromSharedStorage();
      if (active) {
        refreshFromSession();
      }
    };

    window.addEventListener('authenticated', onAuthenticated);
    window.addEventListener('logout', onLogout);
    window.addEventListener('storage', onStorage);
    return () => {
      active = false;
      stopTabSync();
      window.removeEventListener('authenticated', onAuthenticated);
      window.removeEventListener('logout', onLogout);
      window.removeEventListener('storage', onStorage);
    };
  }, [refreshFromSession]);

  const login = useCallback(async (username, password) => {
    const result = await authService.login(username, password);
    setState(buildStateFromSession({ user: result.user, legacy: readLegacyCompatSession() }));
    return result;
  }, []);

  const logout = useCallback(async () => {
    await authService.logout({ user: state.user?.username });
    setState({
      isAuthenticated: false,
      user: null,
      claims: null,
      alumnoCodigos: ''
    });
  }, [state.user]);

  const value = useMemo(() => ({
    ...state,
    sessionReady,
    login,
    logout,
    refreshSession: refreshFromSession
  }), [state, sessionReady, login, logout, refreshFromSession]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
